import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Flame, Star, Palette, Footprints, Watch, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import Skeleton from "@/components/Skeleton";

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="outfit-section" data-testid={testId}>
      <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={12} strokeWidth={1.8} /> {title}
      </div>
      {children}
    </div>
  );
}

export default function TrendsScreen() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trends/feed")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="screen with-nav fade-in" data-testid="trends-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/home")} aria-label="Back" data-testid="trends-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">TRENDS</div>
        <span className="premium-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>WHAT&apos;S WORN NOW</div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.12 }}>
        {data?.season || "This"} 2026.<br />
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>The hot list.</em>
      </h2>
      <p className="body" style={{ marginTop: 0, marginBottom: 22, color: "var(--text-dim)" }}>
        {data?.headline || "A curated snapshot of what tastemakers and the community are actually wearing."}
      </p>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} w="100%" h={120} r={16} />)}
        </div>
      )}

      {!loading && data && (
        <>
          {/* Trending outfits */}
          <Section title="TRENDING OUTFITS THIS WEEK" icon={Flame} testId="trends-outfits">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.trending_outfits?.map((o, i) => (
                <div key={i} className="card" style={{ padding: 14 }} data-testid={`trends-outfit-${i}`}>
                  <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{o.title}</div>
                  <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 4 }}>{o.vibe}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    {o.tags?.map((t) => <span key={t} className="chip" style={{ pointerEvents: "none", fontSize: 10 }}>{t}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Streetwear */}
          <Section title="STREETWEAR TRENDS" icon={TrendingUp} testId="trends-streetwear">
            <ul className="bullet-list">
              {data.streetwear?.map((t, i) => <li key={i} data-testid={`trends-street-${i}`}>{t}</li>)}
            </ul>
          </Section>

          {/* Old Money */}
          <Section title="OLD MONEY TRENDS" icon={TrendingUp} testId="trends-oldmoney">
            <ul className="bullet-list">
              {data.old_money?.map((t, i) => <li key={i} data-testid={`trends-oldmoney-${i}`}>{t}</li>)}
            </ul>
          </Section>

          {/* Celebrity looks */}
          <Section title="CELEBRITY LOOKS" icon={Star} testId="trends-celeb">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.celebrity_looks?.map((c, i) => (
                <div key={i} className="card" style={{ padding: 14 }} data-testid={`trends-celeb-${i}`}>
                  <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{c.name}</div>
                  <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 4 }}>{c.ref}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Colors */}
          <Section title="TRENDING COLORS" icon={Palette} testId="trends-colors">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.colors?.map((c, i) => (
                <div key={i} className="color-chip" data-testid={`trends-color-${i}`}>
                  <span className="color-chip-swatch" style={{ background: c.hex }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="color-chip-name">{c.name}</div>
                    <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.06em", marginTop: 2 }}>
                      {c.use} · {c.hex}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Shoes */}
          <Section title="TRENDING SHOES" icon={Footprints} testId="trends-shoes">
            <ul className="bullet-list">
              {data.shoes?.map((s, i) => <li key={i} data-testid={`trends-shoe-${i}`}>{s}</li>)}
            </ul>
          </Section>

          {/* Accessories */}
          <Section title="TRENDING ACCESSORIES" icon={Watch} testId="trends-accessories">
            <ul className="bullet-list">
              {data.accessories?.map((a, i) => <li key={i} data-testid={`trends-acc-${i}`}>{a}</li>)}
            </ul>
          </Section>

          {/* Community top */}
          {data.community_top?.length > 0 && (
            <Section title="MOST LIKED COMMUNITY OUTFITS" icon={Flame} testId="trends-community">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {data.community_top.slice(0, 6).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => navigate(`/feed/u/${p.user_id}`)}
                    className="card"
                    style={{ padding: 0, overflow: "hidden", aspectRatio: "1 / 1", border: "1px solid var(--line)" }}
                    data-testid={`trends-community-post-${p.id}`}
                  >
                    {p.thumbnail_url || p.image_url ? (
                      <img src={p.thumbnail_url || p.image_url} alt={p.caption || "post"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-elev-2)" }}>
                        <Flame size={20} color="var(--gold)" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
